import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import AdminLayout from '../../components/admin/AdminLayout'
import { supabase } from '../../lib/supabase'
import {
  Users,
  BookOpen,
  Megaphone,
  Activity,
  TrendingUp,
  AlertCircle,
  Shield
} from 'lucide-react'

interface AdminStats {
  totalUsers: number
  totalCourses: number
  totalCampaigns: number
  activeCampaigns: number
  suspendedUsers?: number
  rolesDistribution?: Record<string, number>
  recentActivity?: number
  recentForumPosts?: number
}

export default function AdminDashboard() {
  const { user, hasPermission } = useAuth()
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      setLoading(true)
      setError(null)

      // Llamar a la Edge Function simplificada para obtener estadísticas
      const { data, error: funcError } = await supabase.functions.invoke('get-admin-stats-simple')

      if (funcError) {
        throw funcError
      }

      if (data?.error) {
        throw new Error(data.error.message)
      }

      setStats(data.data)
    } catch (err: any) {
      console.error('Error loading stats:', err)
      setError(err.message || 'Error al cargar estadísticas')
    } finally {
      setLoading(false)
    }
  }

  const hasFullPermission = hasPermission('view_reports')

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Dashboard Administrativo
          </h1>
          <p className="text-gray-600">
            Bienvenido al panel de administración de Learn to Lead Change
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-900">Error al cargar datos</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={loadStats}
                className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Reintentar
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        )}

        {/* Estadísticas */}
        {!loading && stats && (
          <>
            {/* Cards de estadísticas principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="Total Usuarios"
                value={stats.totalUsers}
                icon={Users}
                color="blue"
              />
              <StatCard
                title="Total Cursos"
                value={stats.totalCourses}
                icon={BookOpen}
                color="green"
              />
              <StatCard
                title="Total Campañas"
                value={stats.totalCampaigns}
                icon={Megaphone}
                color="orange"
              />
              <StatCard
                title="Campañas Activas"
                value={stats.activeCampaigns}
                icon={Activity}
                color="purple"
              />
            </div>

            {/* Estadísticas adicionales para admin completo */}
            {hasFullPermission && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {stats.suspendedUsers !== undefined && (
                    <StatCard
                      title="Usuarios Suspendidos"
                      value={stats.suspendedUsers}
                      icon={Shield}
                      color="red"
                    />
                  )}
                  {stats.recentActivity !== undefined && (
                    <StatCard
                      title="Actividad (7 días)"
                      value={stats.recentActivity}
                      icon={TrendingUp}
                      color="indigo"
                    />
                  )}
                  {stats.recentForumPosts !== undefined && (
                    <StatCard
                      title="Posts Recientes"
                      value={stats.recentForumPosts}
                      icon={Activity}
                      color="teal"
                    />
                  )}
                </div>

                {/* Distribución de roles */}
                {stats.rolesDistribution && (
                  <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Distribución de Roles
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(stats.rolesDistribution).map(([role, count]) => (
                        <div key={role} className="text-center p-4 bg-gray-50 rounded-lg">
                          <p className="text-2xl font-bold text-education">{count}</p>
                          <p className="text-sm text-gray-600 capitalize">{role}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Accesos rápidos */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Accesos Rápidos
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {hasPermission('manage_users') && (
                  <QuickLink
                    href="/admin/usuarios"
                    title="Gestionar Usuarios"
                    description="Crear, editar y administrar usuarios"
                    icon={Users}
                  />
                )}
                {hasPermission('manage_courses') && (
                  <QuickLink
                    href="/admin/cursos"
                    title="Gestionar Cursos"
                    description="Administrar contenido educativo"
                    icon={BookOpen}
                  />
                )}
                {hasPermission('manage_campaigns') && (
                  <QuickLink
                    href="/admin/campanas"
                    title="Gestionar Campañas"
                    description="Moderar campañas cívicas"
                    icon={Megaphone}
                  />
                )}
                {hasPermission('moderate_forum') && (
                  <QuickLink
                    href="/admin/moderacion"
                    title="Moderación"
                    description="Moderar contenido y foros"
                    icon={Shield}
                  />
                )}
                {hasPermission('view_reports') && (
                  <QuickLink
                    href="/admin/reportes"
                    title="Ver Reportes"
                    description="Análisis y estadísticas"
                    icon={TrendingUp}
                  />
                )}
                {hasPermission('access_logs') && (
                  <QuickLink
                    href="/admin/logs"
                    title="Logs de Auditoría"
                    description="Historial de acciones"
                    icon={Activity}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  )
}

interface StatCardProps {
  title: string
  value: number
  icon: React.ElementType
  color: string
}

function StatCard({ title, value, icon: Icon, color }: StatCardProps) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    orange: 'bg-orange-500',
    purple: 'bg-purple-500',
    red: 'bg-red-500',
    indigo: 'bg-indigo-500',
    teal: 'bg-teal-500'
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  )
}

interface QuickLinkProps {
  href: string
  title: string
  description: string
  icon: React.ElementType
}

function QuickLink({ href, title, description, icon: Icon }: QuickLinkProps) {
  return (
    <a
      href={href}
      className="block p-4 border border-gray-200 rounded-lg hover:border-education hover:bg-education/5 transition-all"
    >
      <div className="flex items-start space-x-3">
        <div className="p-2 bg-education/10 rounded-lg">
          <Icon className="h-5 w-5 text-education" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 mb-1">{title}</h4>
          <p className="text-xs text-gray-600">{description}</p>
        </div>
      </div>
    </a>
  )
}
